'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Arrow from '@/components/ui/Arrow';
import { Link } from '@/i18n/navigation';
import { cartDiscounts, formatMoney, isDefaultTitle, lineIsDiscounted } from '@/lib/shopify/format';
import { trackBeginCheckout } from '@/lib/analytics';
import { PRODUCT_CLEAN_IMAGE } from '@/lib/shopify/gallery';
import type { CartLine } from '@/lib/shopify/types';
import { useFreeShippingCountry } from './Promo';
import { useShop } from './ShopProvider';

/** Side cart (Shopify Cart API). Payment, address and taxes happen on Shopify Checkout. */
export default function CartDrawer() {
  const t = useTranslations('shop');
  const tAll = useTranslations();
  const locale = useLocale();
  const tPromo = useTranslations('promo');
  const country = useFreeShippingCountry();
  const { product, promo, cart, isCartOpen, closeCart, isPending, notice, setQuantity, removeLine, justAddedId, prepareCheckout } = useShop();
  const panelRef = useRef<HTMLDivElement>(null);
  const lines = cart?.lines ?? [];
  const tab = isCartOpen ? 0 : -1;
  // The panel is always in the DOM (off-screen), so its photo would download on every page load.
  // Mount it only once the cart has been opened.
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (isCartOpen) setHasOpened(true);
  }, [isCartOpen]);

  // Close with Escape, lock page scroll and move focus into the panel while open
  useEffect(() => {
    if (!isCartOpen) return;
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && closeCart();
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isCartOpen, closeCart]);

  // Our product always shows the clean product shot; anything else falls back to its Shopify image
  const lineImage = (line: CartLine) =>
    line.merchandise.product.handle === product?.handle
      ? { url: PRODUCT_CLEAN_IMAGE.url, alt: line.merchandise.product.title }
      : (() => {
          const img = line.merchandise.image ?? line.merchandise.product.featuredImage;
          return img ? { url: img.url, alt: img.altText || line.merchandise.product.title } : null;
        })();

  const productHref = product ? `/products/${product.handle}` : '/';

  return (
    <div className={`fixed inset-0 z-[60] ${isCartOpen ? '' : 'pointer-events-none'}`} aria-hidden={!isCartOpen}>
      {/* Backdrop */}
      <div
        onClick={closeCart}
        className={`absolute inset-0 bg-[rgba(11,9,7,0.6)] backdrop-blur-sm transition-opacity duration-500 ${
          isCartOpen ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <div
        ref={panelRef}
        // Entrance choreography (tailwind.css, .cart-stagger / .cart-rise / .cart-sheen) plays on each open
        data-open={isCartOpen}
        role="dialog"
        aria-modal="true"
        aria-label={t('cartTitle')}
        tabIndex={-1}
        className={`absolute top-0 right-0 h-full w-full sm:w-[max(36rem,440px)] bg-[var(--ink)] border-l border-[var(--line)] flex flex-col outline-none transition-transform duration-500 ${
          isCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ transitionTimingFunction: 'var(--ease-out)' }}
      >
        {/* Header — same height as the site header */}
        <div className="flex items-center justify-between px-6 sm:px-8 h-14 md:h-16 lg:h-[70px] border-b border-[var(--line)] shrink-0">
          <p className="flex items-baseline gap-3">
            <span className="display text-[max(1.6rem,19px)] leading-none">{t('cartTitle')}</span>
            {cart && cart.totalQuantity > 0 && (
              <span className="label-mono text-copper">
                {String(cart.totalQuantity).padStart(2, '0')}
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={closeCart}
            aria-label={t('close')}
            tabIndex={tab}
            className="w-10 h-10 -mr-2 flex items-center justify-center text-[rgba(242,235,224,0.85)] hover:text-[var(--copper)] transition-colors duration-300"
          >
            <Icon name="XMarkIcon" size={18} variant="outline" />
          </button>
        </div>

        {/* Lines */}
        <div className={`flex-1 overflow-y-auto transition-opacity duration-300 ${isPending ? 'opacity-60' : ''}`}>
          {lines.length === 0 ? (
            // Empty cart: the product itself, big, with a way back to it
            <div className="min-h-full flex flex-col">
              <div className="relative w-full aspect-[5/4] sm:aspect-[4/3] shrink-0 overflow-hidden">
                {hasOpened && (
                  <AppImage
                    src={PRODUCT_CLEAN_IMAGE.url}
                    alt=""
                    fill
                    sizes="(max-width: 640px) 100vw, 440px"
                    style={{ objectFit: 'cover', objectPosition: '50% 40%' }}
                  />
                )}
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, var(--ink) 0%, rgba(11,9,7,0.35) 45%, transparent 75%)' }}
                />
              </div>
              <div className="px-6 sm:px-8 pb-10 -mt-6 relative">
                <p className="display display-sm">{isPending ? t('adding') : t('empty')}</p>
                {!isPending && (
                  <Link href={productHref} onClick={closeCart} tabIndex={tab} className="btn btn-solid w-full mt-8">
                    {tAll('hero.ctaProduct')}
                    <Arrow />
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <ul className="cart-stagger px-6 sm:px-8">
              {lines.map((line, i) => {
                const { merchandise } = line;
                const image = lineImage(line);
                return (
                  <li
                    key={line.id}
                    style={{ '--i': i } as React.CSSProperties}
                    className={`grid grid-cols-[6.5rem_1fr] gap-4 min-[380px]:grid-cols-[max(7.5rem,96px)_1fr] min-[380px]:gap-5 py-7 border-b border-[var(--line)] ${
                      merchandise.id === justAddedId ? 'cart-line-new' : ''
                    }`}
                  >
                    {/* Product image — clean shot, 4:5 */}
                    <Link
                      href={productHref}
                      onClick={closeCart}
                      tabIndex={tab}
                      className="relative block aspect-[4/5] overflow-hidden bg-[var(--ink-2)] group"
                    >
                      {image && (
                        <AppImage
                          src={image.url}
                          alt={image.alt}
                          fill
                          sizes="130px"
                          style={{ objectFit: 'cover' }}
                          className="transition-transform duration-700 group-hover:scale-[1.04]"
                        />
                      )}
                    </Link>

                    <div className="flex flex-col min-w-0">
                      <p className="label-mono text-copper">Nomafé</p>
                      <p className="display text-[max(1.45rem,16px)] leading-snug mt-1.5">
                        {merchandise.product.title}
                      </p>
                      {!isDefaultTitle(merchandise.title) && (
                        <p className="label-mono text-[var(--smoke)] mt-1.5">{merchandise.title}</p>
                      )}
                      <p className="label-mono text-[var(--ash)] mt-2">
                        {formatMoney(merchandise.price, locale)} {t('each')}
                      </p>

                      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 mt-auto pt-4">
                        {/* Quantity */}
                        <div>
                          <div className="flex items-center border border-[var(--line)]">
                            <button
                              type="button"
                              onClick={() => setQuantity(line.id, line.quantity - 1)}
                              disabled={isPending}
                              aria-label={t('decrease')}
                              tabIndex={tab}
                              className="w-[max(2.4rem,34px)] h-[max(2.4rem,34px)] flex items-center justify-center text-[var(--smoke)] hover:text-[var(--cream)] disabled:opacity-40 transition-colors"
                            >
                              <span aria-hidden="true">−</span>
                            </button>
                            <span className="label-mono w-[max(2rem,28px)] text-center" aria-label={t('quantity')}>
                              {line.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => setQuantity(line.id, line.quantity + 1)}
                              disabled={isPending || !merchandise.availableForSale}
                              aria-label={t('increase')}
                              tabIndex={tab}
                              className="w-[max(2.4rem,34px)] h-[max(2.4rem,34px)] flex items-center justify-center text-[var(--smoke)] hover:text-[var(--cream)] disabled:opacity-40 transition-colors"
                            >
                              <span aria-hidden="true">+</span>
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeLine(line.id)}
                            disabled={isPending}
                            tabIndex={tab}
                            className="label-mono text-[var(--ash)] hover:text-[var(--cream)] border-b border-transparent hover:border-[var(--copper)] pb-0.5 mt-3 transition-colors disabled:opacity-40"
                          >
                            {t('remove')}
                          </button>
                        </div>
                        <p className="display text-[max(1.6rem,18px)] leading-none whitespace-nowrap text-right ml-auto">
                          {/* Before/after discount: both amounts come straight from Shopify */}
                          {lineIsDiscounted(line) && (
                            <s className="block text-[0.62em] text-[var(--ash)] mb-1.5">
                              {formatMoney(line.cost.subtotalAmount, locale)}
                            </s>
                          )}
                          {formatMoney(line.cost.totalAmount, locale)}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Summary + checkout */}
        {(lines.length > 0 || notice) && (
          <div className="cart-rise shrink-0 border-t border-[var(--line)] bg-[var(--ink-2)] px-6 sm:px-8 pt-6 pb-[max(24px,env(safe-area-inset-bottom))]">
            {notice && (
              <p role="status" className="body-copy text-[max(0.92rem,12.5px)] text-copper mb-5">
                {notice === 'error' ? t('error') : t('stockWarning')}
              </p>
            )}
            {cart && lines.length > 0 && (
              <>
                {/* Discounts exactly as Shopify applied them (name and amount); nothing is hardcoded here */}
                {cartDiscounts(cart).map((discount) => (
                  <div key={discount.label ?? 'discount'} className="flex items-baseline justify-between gap-4 mb-2.5">
                    <span className="text-[13.5px] text-copper">{discount.label ?? tPromo('discountFallback')}</span>
                    <span className="text-[13.5px] text-copper whitespace-nowrap">−{formatMoney(discount.amount, locale)}</span>
                  </div>
                ))}
                {promo.freeShipping && (
                  <div className="flex items-baseline justify-between gap-4 mb-4">
                    <span className="text-[13.5px] text-[var(--smoke)]">{tPromo('shippingRow')}</span>
                    <span className="text-[13.5px] text-copper text-right">
                      {tPromo('free')} · {country}
                    </span>
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-[13.5px] text-[var(--smoke)]">{t('subtotal')}</span>
                  <span className="display text-[max(2.2rem,24px)] leading-none">
                    {formatMoney(cart.cost.subtotalAmount, locale)}
                  </span>
                </div>
                <p className="body-copy text-[max(0.85rem,12px)] text-[var(--ash)] mt-3 mb-6">
                  {promo.freeShipping ? tPromo('cartNote', { country }) : t('taxesNote')}
                </p>
                <a
                  href={cart.checkoutUrl}
                  tabIndex={tab}
                  aria-disabled={isPending}
                  onClick={async (e) => {
                    e.preventDefault();
                    if (isPending) return;
                    trackBeginCheckout(cart);
                    // Wait for the analytics attributes (saved, or cleared without consent) before leaving
                    await prepareCheckout(cart);
                    if (e.ctrlKey || e.metaKey || e.shiftKey) window.open(cart.checkoutUrl, '_blank', 'noopener');
                    else window.location.href = cart.checkoutUrl;
                  }}
                  className={`btn btn-solid w-full ${justAddedId ? 'cart-sheen' : ''} ${isPending ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  {t('checkout')}
                  <Arrow />
                </a>
                <div className="flex items-center justify-between gap-4 mt-4">
                  <span className="label-mono text-[var(--ash)] flex items-center gap-2">
                    <Icon name="LockClosedIcon" size={11} variant="outline" />
                    {tAll('productPage.securePayment')}
                  </span>
                  <button
                    type="button"
                    onClick={closeCart}
                    tabIndex={tab}
                    className="label-mono text-[var(--smoke)] hover:text-[var(--cream)] border-b border-transparent hover:border-[var(--copper)] pb-0.5 transition-colors shrink-0"
                  >
                    {t('keepShopping')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
